'use client';

import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Dialog,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { useT } from '@/hooks';
import { useAuth } from '@/hooks/useAuth';
import { useCreateAlert } from '@/services/query';
import type { EventResponse } from '@/types';

interface MarketAlertDialogProps {
  open: boolean;
  onClose: () => void;
  event: EventResponse | null;
}

interface OutcomeOpt {
  name: string;
  tokenId: string;
  price: number;
}
interface MarketOpt {
  key: string;
  question: string;
  marketId?: string;
  marketSlug?: string;
  outcomes: OutcomeOpt[];
}

const safeJson = (s: string) => {
  try {
    return JSON.parse(s);
  } catch {
    return [];
  }
};

/**
 * Defensively extract outcomes (+ token ids + current price) from a market.
 * Mirrors the parser in the Portfolio CreateAlertDialog, but this dialog is
 * seeded from the market the user is currently viewing rather than their
 * bookmarks, so an alert can be created straight from the market detail page.
 */
const parseOutcomes = (market: any): OutcomeOpt[] => {
  let raw = market?.outcomes;
  if (typeof raw === 'string') raw = safeJson(raw);
  if (!Array.isArray(raw)) return [];
  const tokenIds =
    typeof market?.clobTokenIds === 'string'
      ? safeJson(market.clobTokenIds)
      : market?.clobTokenIds || [];
  const prices =
    typeof market?.outcomePrices === 'string'
      ? safeJson(market.outcomePrices)
      : market?.outcomePrices || [];
  return raw
    .map((o: any, i: number) => {
      if (o && typeof o === 'object') {
        return {
          name: String(o.name ?? `Outcome ${i + 1}`),
          tokenId: String(o.id ?? tokenIds[i] ?? ''),
          price: Number(o.price ?? o.midpoint ?? prices[i] ?? 0),
        };
      }
      return { name: String(o), tokenId: String(tokenIds[i] ?? ''), price: Number(prices[i] ?? 0) };
    })
    .filter((o: OutcomeOpt) => o.tokenId);
};

const seedThreshold = (price: number) => (price > 0 ? String(Math.round(price * 100)) : '60');

/**
 * Create a price alert for the market currently being viewed. The market/outcome
 * list is derived from the event's markets, so the user can set an alert without
 * first bookmarking the market. Selection is fully derived (no setState-in-effect);
 * explicit user choices win.
 */
export const MarketAlertDialog = ({ open, onClose, event }: MarketAlertDialogProps) => {
  const { t } = useT();
  const { isAuthenticated } = useAuth();
  const createAlert = useCreateAlert();

  const markets = useMemo<MarketOpt[]>(() => {
    const list = (event as any)?.markets ?? [];
    const opts: MarketOpt[] = [];
    for (const m of list) {
      if (m?.archived) continue;
      const outcomes = parseOutcomes(m);
      if (!outcomes.length) continue;
      opts.push({
        key: String(m.id ?? m.conditionId),
        question: String(m.groupItemTitle || m.question || event?.title || 'Market'),
        marketId: m.conditionId ?? m.id,
        marketSlug: event?.slug ?? m.slug,
        outcomes,
      });
    }
    return opts;
  }, [event]);

  // User selections (empty until they explicitly pick) — defaults are derived.
  const [marketKey, setMarketKey] = useState('');
  const [outcomeToken, setOutcomeToken] = useState('');
  const [direction, setDirection] = useState<'above' | 'below'>('above');
  const [thresholdPct, setThresholdPct] = useState('');

  const effectiveMarketKey = marketKey || markets[0]?.key || '';
  const selectedMarket = markets.find((m) => m.key === effectiveMarketKey);
  const effectiveOutcomeToken = outcomeToken || selectedMarket?.outcomes[0]?.tokenId || '';
  const selectedOutcome = selectedMarket?.outcomes.find((o) => o.tokenId === effectiveOutcomeToken);
  const effectiveThreshold = thresholdPct || seedThreshold(selectedOutcome?.price ?? 0);

  const handleMarketChange = (key: string) => {
    setMarketKey(key);
    const first = markets.find((m) => m.key === key)?.outcomes[0];
    setOutcomeToken(first?.tokenId ?? '');
    setThresholdPct(seedThreshold(first?.price ?? 0));
  };

  const handleOutcomeChange = (token: string) => {
    setOutcomeToken(token);
    const o = selectedMarket?.outcomes.find((x) => x.tokenId === token);
    setThresholdPct(seedThreshold(o?.price ?? 0));
  };

  const handleCreate = () => {
    if (!selectedMarket || !selectedOutcome) return;
    const pct = Number(effectiveThreshold);
    if (!Number.isFinite(pct) || pct < 1 || pct > 99) {
      toast.error(t('portfolio.dashboard.alertThresholdError', { defaultValue: 'Enter 1–99%' }));
      return;
    }
    createAlert.mutate(
      {
        tokenId: selectedOutcome.tokenId,
        threshold: pct / 100,
        direction,
        marketId: selectedMarket.marketId,
        marketSlug: selectedMarket.marketSlug,
        question: selectedMarket.question,
        outcome: selectedOutcome.name,
      },
      {
        onSuccess: () => {
          toast.success(t('portfolio.dashboard.alertCreated', { defaultValue: 'Alert created' }));
          onClose();
        },
        onError: () =>
          toast.error(
            t('portfolio.dashboard.alertCreateError', { defaultValue: 'Could not create alert' }),
          ),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
          {t('portfolio.dashboard.newAlert', { defaultValue: 'New price alert' })}
        </Typography>

        {!isAuthenticated ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {t('portfolio.dashboard.alertLoginRequired', {
                defaultValue: 'Log in to set price alerts.',
              })}
            </Typography>
          </Box>
        ) : markets.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {t('portfolio.dashboard.alertNoOutcomes', {
                defaultValue: 'No tradable outcomes to alert on for this market.',
              })}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {markets.length > 1 && (
              <TextField
                select
                label={t('portfolio.dashboard.alertMarket', { defaultValue: 'Market' })}
                value={effectiveMarketKey}
                onChange={(e) => handleMarketChange(e.target.value)}
                size="small"
                fullWidth
              >
                {markets.map((m) => (
                  <MenuItem key={m.key} value={m.key} sx={{ whiteSpace: 'normal' }}>
                    {m.question}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              select
              label={t('portfolio.dashboard.alertOutcome', { defaultValue: 'Outcome' })}
              value={effectiveOutcomeToken}
              onChange={(e) => handleOutcomeChange(e.target.value)}
              size="small"
              fullWidth
            >
              {(selectedMarket?.outcomes ?? []).map((o) => (
                <MenuItem key={o.tokenId} value={o.tokenId}>
                  {o.name} · {(o.price * 100).toFixed(0)}%
                </MenuItem>
              ))}
            </TextField>

            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {t('portfolio.dashboard.alertWhen', { defaultValue: 'Notify me when price goes' })}
              </Typography>
              <ToggleButtonGroup
                value={direction}
                exclusive
                onChange={(_, v) => v && setDirection(v)}
                fullWidth
                size="small"
                sx={{ mt: 0.75 }}
              >
                <ToggleButton value="above" sx={{ textTransform: 'none', fontWeight: 700 }}>
                  {t('portfolio.dashboard.alertAbove', { defaultValue: 'Above' })}
                </ToggleButton>
                <ToggleButton value="below" sx={{ textTransform: 'none', fontWeight: 700 }}>
                  {t('portfolio.dashboard.alertBelow', { defaultValue: 'Below' })}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <TextField
              label={t('portfolio.dashboard.alertThreshold', { defaultValue: 'Threshold (%)' })}
              value={effectiveThreshold}
              onChange={(e) => setThresholdPct(e.target.value.replace(/[^0-9]/g, ''))}
              size="small"
              fullWidth
              inputProps={{ inputMode: 'numeric' }}
            />

            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={createAlert.isPending || !selectedOutcome}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2.5, py: 1.25 }}
            >
              {t('portfolio.dashboard.createAlert', { defaultValue: 'Create alert' })}
            </Button>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};
